import React, { useMemo, useRef } from "react";
import { List, Spin, Button, Input, Avatar, Typography, Tag } from "antd";
import { PaperClipOutlined, CloseOutlined } from "@ant-design/icons";
import ChatMessage from "./ChatMessage";
import dayjs from "dayjs";

const { TextArea } = Input;

const ChatPanel = ({
  // data
  messages = [],
  isLoading,
  isTyping,
  sessionId,
  colorPrimary,
  userId,
  activeUsers = [],

  // edit state
  editMsg,
  editingMessage,
  setEditingMessage,
  onCancelEdit,

  // input
  newMessage,
  onMessageChange,
  onSend,

  // file
  onFileClick,
  fileInputRef,
  onFileChange,

  // actions per message
  onRightClick,
  onEditAction,
  onDeleteAction,

  // mention (optional — only for InternalChat)
  showMentionDropdown,
  mentionResults,
  mentionLoading,
  onMentionSelect,
  taggedUsers,
  onRemoveTag,

  // scroll
  messagesEndRef,
  onScroll,
  textareaRef,

  // partners header (optional — only for Chat)
  headerRight,

  // send button disabled guard
  sendDisabled,
  pendingFile,
  onClearPendingFile,
  isMobile = false,
}) => {
  const stringToColor = (str) => {
    if (!str) return "#667085";
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return `hsl(${hash % 360}, 65%, 55%)`;
  };

  const getInitial = (name) => name?.trim()?.charAt(0)?.toUpperCase() || "?";

  const displayMessages = useMemo(
    () =>
      (messages || []).filter((msg) => !["active_users"].includes(msg?.message_type)),
    [messages]
  );

  const TodayDivider = () => (
    <div
      className="chat-today-divider"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 0",
      }}
    >
      <div style={{ flex: 1, height: 1, background: "#E5E7EB" }} />
      <Typography.Text style={{ fontSize: 12, color: "#6B7280" }}>
        Today
      </Typography.Text>
      <div style={{ flex: 1, height: 1, background: "#E5E7EB" }} />
    </div>
  );

  return (
    <div className="chat-panel-root">
      {/* Header */}
      {/* <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          flexShrink: 0,
        }}
      >
        <Typography.Title
          level={4}
          style={{ margin: 0, fontWeight: 900, fontSize: 20, color: "#1D2939" }}
        >
          {sessionId}
        </Typography.Title> */}
      {headerRight}
      {/* </div> */}

      {/* Messages */}
      <div
        ref={messagesEndRef}
        onScroll={onScroll}
        className="chat-container"
        style={{
          flex: 1,
          overflowY: "auto",
          border: "none",
          borderRadius: 0,
          padding: isMobile ? undefined : "0 16px",
          marginBottom: isMobile ? 0 : 12,
          backgroundColor: isMobile ? undefined : "#fff",
          position: "relative",
        }}
      >
        {isLoading && displayMessages.length === 0 ? (
          <div className="chat-initial-loading">
            <Spin />
          </div>
        ) : (
          <>
            {isLoading && (
              <div style={{ textAlign: "center", padding: 10 }}>
                <Spin size="small" />
              </div>
            )}

            <List
              className="chat-list"
              itemLayout="horizontal"
              dataSource={displayMessages}
              locale={{ emptyText: "No messages yet" }}
              renderItem={(msg, index) => {
                const createdAt = msg?.created_at;
                const isToday =
                  createdAt != null && dayjs(createdAt).isSame(dayjs(), "day");
                const prev = displayMessages[index - 1];
                const prevIsToday =
                  prev?.created_at != null &&
                  dayjs(prev.created_at).isSame(dayjs(), "day");
                const showTodayDivider = isToday && !prevIsToday;

                return (
                  <React.Fragment key={msg?.id ?? index}>
                    {showTodayDivider && <TodayDivider />}
                    <ChatMessage
                      msg={msg}
                      userId={userId}
                      activeUsers={activeUsers}
                      editMsg={editMsg}
                      editingMessage={editingMessage}
                      setEditingMessage={setEditingMessage}
                      onSend={onSend}
                      onRightClick={onRightClick}
                      onEditAction={onEditAction}
                      onDeleteAction={onDeleteAction}
                      textareaRef={textareaRef}
                    />
                  </React.Fragment>
                );
              }}
            />
          </>
        )}

        {isTyping && (
          <div
            style={{
              color: "#9CA3AF",
              fontStyle: "italic",
              fontSize: 13,
              padding: "4px 8px",
            }}
          >
            Someone is typing…
          </div>
        )}
      </div>

      {/* Input area */}
      <div className="chat-panel-input-row">
        {/* Mention dropdown */}
        {showMentionDropdown && (
          <div
            style={{
              position: "absolute",
              bottom: "100%",
              left: 0,
              right: 0,
              backgroundColor: "#fff",
              border: "1px solid #E5E7EB",
              borderRadius: 10,
              boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
              zIndex: 1000,
              maxHeight: 200,
              overflowY: "auto",
              marginBottom: 6,
            }}
          >
            {mentionLoading ? (
              <div style={{ padding: "10px 14px", textAlign: "center" }}>
                <Spin size="small" />
              </div>
            ) : mentionResults?.length === 0 ? (
              <div
                style={{ padding: "10px 14px", color: "#9CA3AF", fontSize: 13 }}
              >
                No users found
              </div>
            ) : (
              mentionResults.map((user) => (
                <div
                  key={user.user__id}
                  onClick={() => onMentionSelect?.(user)}
                  style={{
                    padding: "8px 14px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    fontSize: 14,
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#F3F4F6")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  <Avatar
                    size={28}
                    style={{
                      backgroundColor: stringToColor(user.first_name),
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {getInitial(user.first_name)}
                  </Avatar>
                  <div>
                    <Typography.Text strong style={{ fontSize: 13 }}>
                      {user.first_name} {user.last_name}
                    </Typography.Text>
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: 11, marginLeft: 6 }}
                    >
                      {user.role}
                    </Typography.Text>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Tagged user chips */}
        {taggedUsers?.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: 4,
              flexWrap: "wrap",
              marginBottom: 6,
            }}
          >
            {taggedUsers.map((u) => (
              <Tag
                key={u.user__id}
                closable
                onClose={() => onRemoveTag?.(u.user__id)}
                style={{ borderRadius: 12 }}
              >
                @{u.first_name} {u.last_name}
              </Tag>
            ))}
          </div>
        )}

        {/* Input row */}
        {pendingFile && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              marginBottom: 8,
              padding: "8px 10px",
              border: "1px solid #E5E7EB",
              borderRadius: 12,
              background: "#F9FAFB",
            }}
          >
            <Typography.Text
              style={{ fontSize: 13, color: "#111827" }}
              ellipsis={{ tooltip: pendingFile?.name }}
            >
              {pendingFile?.name}
            </Typography.Text>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => onClearPendingFile?.()}
            />
          </div>
        )}
        <div
          className="chat-panel-input"
          style={{
            display: "flex",
            alignItems: "end",
            border: "1px solid #E5E7EB",
            borderRadius: 8,
            padding: "4px 16px",
            background: "#fff",
          }}
        >
          <TextArea
            value={editMsg ? editingMessage : newMessage}
            onChange={(e) =>
              editMsg ? setEditingMessage(e.target.value) : onMessageChange(e)
            }
            placeholder={
              isMobile
                ? showMentionDropdown !== undefined
                  ? "Message… use @ to mention"
                  : "Type a message…"
                : showMentionDropdown !== undefined
                  ? "Type your message… Use @ to mention someone"
                  : "Type your message…"
            }
            autoSize={{ minRows: isMobile ? 1 : 4, maxRows: isMobile ? 3 : 6 }}
            bordered={false}
            style={{ flex: 1, resize: "none", boxShadow: "none" }}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
          />

          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept="image/*,.pdf,.doc,.docx"
            onChange={onFileChange}
          />

          <div>
            <Button
              type="text"
              icon={<PaperClipOutlined />}
              onClick={onFileClick}
              disabled={sendDisabled === true && !editMsg}
              title="Attach File"
            />
            <Typography.Text
              onClick={onSend}
              disabled={
                sendDisabled ||
                (editMsg
                  ? !editingMessage.trim()
                  : !newMessage.trim() && !pendingFile)
              }
              style={{
                color: colorPrimary,
                borderRadius: 10,
                padding: "0 20px",
                height: 32,
                fontWeight: 500,
                cursor:
                  sendDisabled ||
                  (editMsg
                    ? !editingMessage.trim()
                    : !newMessage.trim() && !pendingFile)
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              Send
            </Typography.Text>
            {editMsg && (
              <Button
                type="text"
                shape="circle"
                icon={<CloseOutlined />}
                onClick={onCancelEdit}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
